<template>
  <AltModal :open="etat.ouverte" title="Nouvelle demande" size="lg" @update:open="(v: boolean) => !v && fermer()">
    <form id="form-nouvelle-demande" class="space-y-5" @submit.prevent="envoyer">
      <!-- Le formulaire de création de dossier Inqom (DEC-044) : aux seuls collaborateurs du groupe. -->
      <div v-if="peutInqom" class="flex justify-center">
        <BasculeSegment v-model="formulaire" :options="optionsFormulaire" label="Type de demande" />
      </div>

      <!-- Pour qui -->
      <fieldset v-if="peutPourCompte && !inqom" class="space-y-3" data-tour="nd-pour-qui">
        <legend class="mb-2 text-sm font-medium text-space-indigo dark:text-altoneo-50">Pour qui ?</legend>
        <BasculeSegment v-model="pourQui" :options="optionsPourQui" label="Pour qui est la demande" />

        <template v-if="pourCompte">
          <AltFormField label="Société" required :error="erreurs.contactId" v-slot="{ id: idChamp }">
            <AltInput :id="idChamp" v-model="recherche" placeholder="Raison sociale ou code dossier (2 caractères au moins)" />
          </AltFormField>
          <ul
            v-if="clients.length && !clientChoisi"
            class="max-h-56 overflow-y-auto rounded-lg border border-slate-200 dark:border-alt-dark-border"
          >
            <li v-for="c in clients" :key="c.id">
              <button type="button" class="flex w-full justify-between px-3 py-2 text-left text-sm pa-survol" @click="choisirClient(c)">
                <span class="text-slate-800 dark:text-altoneo-50">{{ c.nom }}</span>
                <span class="text-xs text-slate-600 dark:text-alt-dark-muted">{{ c.numero }}{{ c.interne ? ' · groupe' : '' }}</span>
              </button>
            </li>
          </ul>
          <div v-if="clientChoisi" class="flex items-center gap-2 text-sm">
            <AltBadge variant="navy" size="sm">{{ clientChoisi.nom }}</AltBadge>
            <AltButton size="sm" variant="ghost" @click="clientChoisi = null">Changer</AltButton>
          </div>
          <AltFormField v-if="clientChoisi" label="Contact demandeur" required v-slot="{ id: idChamp }">
            <AltSelect
              :id="idChamp"
              v-model="contactId"
              :options="contacts.map((c) => ({ value: c.id, label: c.email ? `${c.nom} — ${c.email}` : c.nom }))"
              placeholder="Choisir le contact"
            />
          </AltFormField>
        </template>
      </fieldset>

      <AltFormField v-if="!pourCompte && societes.length > 1" v-slot="{ id: idChamp }" label="Société" required :error="erreurs.clientId">
        <AltSelect :id="idChamp" v-model="clientId" :options="societes.map((s) => ({ value: s.clientId, label: s.nom }))" />
      </AltFormField>

      <AltAlert
        v-if="!pourCompte && societesChargees && societes.length === 0"
        variant="warning"
        message="Votre adresse n'est rattachée à aucune société dans l'annuaire : IT Solutions doit vous rattacher, ou ouvrir la demande pour vous."
      />

      <FormulaireDossierInqom v-if="inqom" v-model="dossier" :erreurs="erreurs" />

      <div v-if="!inqom" class="grid gap-4 sm:grid-cols-2" data-tour="nd-type-urgence">
        <AltFormField label="Type" required :error="erreurs.typeId" v-slot="{ id: idChamp }">
          <AltSelect :id="idChamp" v-model="typeId" :options="(refs?.types ?? []).map((t) => ({ value: String(t.id), label: t.libelle }))" />
        </AltFormField>
        <AltFormField label="Urgence" required :error="erreurs.urgenceId" hint="Bloquante : vous ne pouvez plus travailler." v-slot="{ id: idChamp }">
          <AltSelect :id="idChamp" v-model="urgenceId" :options="(refs?.urgences ?? []).map((u) => ({ value: String(u.id), label: u.libelle }))" />
        </AltFormField>
      </div>

      <div v-if="!inqom" class="space-y-5" data-tour="nd-texte">
        <AltFormField label="Objet" required :error="erreurs.objet" hint="En une phrase : ce qui ne marche pas." v-slot="{ id: idChamp }">
          <AltInput :id="idChamp" v-model="objet" maxlength="120" />
        </AltFormField>
        <AltFormField label="Description" required :error="erreurs.description" v-slot="{ id: idChamp }">
          <AltTextarea :id="idChamp" v-model="description" :rows="6" />
        </AltFormField>
      </div>

      <div data-tour="nd-pieces">
        <label for="fichiers-demande" class="mb-1 block text-sm font-semibold text-altoneo-800 dark:text-altoneo-50">
          Pièces jointes <span class="font-normal text-slate-600 dark:text-alt-dark-muted">(facultatif — 10 au plus, 20 Mo chacune)</span>
        </label>
        <input
          id="fichiers-demande"
          :key="ouverture"
          type="file"
          multiple
          :accept="PJ_EXTENSIONS.map((e) => `.${e}`).join(',')"
          class="block w-full text-sm text-slate-700 file:mr-3 file:rounded-md file:border-0 file:bg-space-indigo file:px-3 file:py-1.5 file:text-white dark:text-altoneo-50 dark:file:bg-sunflower-gold dark:file:text-space-indigo"
          @change="(e) => (fichiers = Array.from((e.target as HTMLInputElement).files ?? []))"
        />
        <p v-for="m in refusFichiers" :key="m" class="mt-1 text-sm text-red-700 dark:text-red-300">{{ m }}</p>
      </div>

      <template v-if="peutPourCompte && !inqom">
        <AltCheckbox v-model="periodique" label="Rendre périodique : recréer cette demande selon une règle" />
        <ReglePeriodicite v-if="periodique" v-model="regle" />
      </template>
    </form>

    <template #footer>
      <div class="flex justify-end gap-2" data-tour="nd-envoyer">
        <AltButton variant="ghost" @click="fermer">Annuler</AltButton>
        <AltButton type="submit" form="form-nouvelle-demande" :loading="envoi">Envoyer la demande</AltButton>
      </div>
    </template>
  </AltModal>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, reactive, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import { useToast } from 'altoneo-charting';
import {
  PJ_EXTENSIONS,
  PJ_NOMBRE_MAX_PAR_ENVOI,
  canHandleRequests,
  dossierInqomVide,
  peutDemanderDossierInqom,
  type DossierInqomSaisie,
  refusPieceJointe,
  refusRegle,
  type DemandeDTO,
  type ReferentielsDTO,
  type Regle,
} from '@its/shared';
import BasculeSegment from '@/components/BasculeSegment.vue';
import FormulaireDossierInqom from '@/components/FormulaireDossierInqom.vue';
import ReglePeriodicite from '@/components/ReglePeriodicite.vue';
import { useNouvelleDemande } from '@/composables/useNouvelleDemande';
import { api, ApiError } from '@/lib/api';
import { useAuthStore } from '@/stores/auth';
import { useDemandesStore } from '@/stores/demandes';

const router = useRouter();
const toast = useToast();
const auth = useAuthStore();
const store = useDemandesStore();
const { etat, fermer } = useNouvelleDemande();

const peutPourCompte = computed(() => canHandleRequests(auth.roles));
/** La règle vit dans @its/shared : l'API refuse le formulaire à qui ne le voit pas ici. */
const peutInqom = computed(() => peutDemanderDossierInqom(auth.user?.kind));
const formulaire = ref<'assistance' | 'inqom'>('assistance');
const optionsFormulaire: { value: 'assistance' | 'inqom'; label: string }[] = [
  { value: 'assistance', label: 'Demande d’assistance' },
  { value: 'inqom', label: 'Création de dossier Inqom' },
];
const inqom = computed(() => peutInqom.value && formulaire.value === 'inqom');
const dossier = ref<DossierInqomSaisie>(dossierInqomVide());
const pourQui = ref<'moi' | 'client'>('moi');
// Un dossier Inqom se demande pour sa propre société, jamais pour le compte d'un client.
const pourCompte = computed(() => !inqom.value && pourQui.value === 'client');
const optionsPourQui: { value: 'moi' | 'client'; label: string }[] = [
  { value: 'moi', label: 'Pour moi' },
  { value: 'client', label: 'Pour le compte d’un client' },
];

const refs = ref<ReferentielsDTO | null>(null);
const societes = ref<{ contactId: string; clientId: string; nom: string }[]>([]);
const societesChargees = ref(false);

const clientId = ref('');
const typeId = ref('');
const urgenceId = ref('');
const objet = ref('');
const description = ref('');
const envoi = ref(false);
const fichiers = ref<File[]>([]);
const refusFichiers = computed(() => [
  ...(fichiers.value.length > PJ_NOMBRE_MAX_PAR_ENVOI ? ['Dix fichiers au plus.'] : []),
  ...fichiers.value.map((f) => refusPieceJointe(f.name, f.size)).filter((m): m is string => m !== null),
]);
const periodique = ref(false);
const regle = ref<Regle>({ frequence: 'hebdomadaire', joursSemaine: [1], jourMois: 1, mois: 1 });
const erreurs = reactive<Record<string, string>>({});
/** Change à chaque ouverture : remonte le champ fichier, qu'on ne peut pas vider autrement. */
const ouverture = ref(0);

// Pour le compte d'un client.
const recherche = ref('');
const clients = ref<{ id: string; nom: string; numero: string; interne: boolean }[]>([]);
const clientChoisi = ref<{ id: string; nom: string } | null>(null);
const contacts = ref<{ id: string; nom: string; email: string | null }[]>([]);
const contactId = ref('');

let minuterie: ReturnType<typeof setTimeout> | undefined;
watch(recherche, (q) => {
  clearTimeout(minuterie);
  if (q.trim().length < 2) {
    clients.value = [];
    return;
  }
  minuterie = setTimeout(async () => {
    clients.value = await api.get<typeof clients.value>(`/clients/recherche?q=${encodeURIComponent(q.trim())}`).catch(() => []);
  }, 250);
});

async function choisirClient(c: { id: string; nom: string }): Promise<void> {
  clientChoisi.value = c;
  contactId.value = '';
  contacts.value = await api.get<typeof contacts.value>(`/clients/${c.id}/contacts`).catch(() => []);
}

/**
 * Chaque ouverture repart d'un formulaire vierge : une demande abandonnée ne
 * doit pas ressurgir, à moitié remplie, dans la suivante.
 */
async function preparer(): Promise<void> {
  for (const k of Object.keys(erreurs)) delete erreurs[k];
  pourQui.value = 'moi';
  formulaire.value = 'assistance';
  dossier.value = dossierInqomVide();
  objet.value = '';
  description.value = '';
  fichiers.value = [];
  periodique.value = etat.periodique && peutPourCompte.value;
  regle.value = { frequence: 'hebdomadaire', joursSemaine: [1], jourMois: 1, mois: 1 };
  recherche.value = '';
  clientChoisi.value = null;
  contactId.value = '';
  ouverture.value++;

  refs.value = await store.chargerReferentiels();
  // Valeurs par défaut : le type le plus courant, l'urgence la plus basse.
  typeId.value = String(refs.value.types.find((t) => t.code === 'logiciel')?.id ?? refs.value.types[0]?.id ?? '');
  urgenceId.value = String(refs.value.urgences[0]?.id ?? '');
  if (!societesChargees.value) {
    societes.value = await api.get<typeof societes.value>('/demandes/mes-societes').catch(() => []);
    societesChargees.value = true;
  }
  clientId.value = societes.value.length === 1 ? societes.value[0]!.clientId : '';
}

watch(
  () => etat.ouverte,
  (ouverte) => {
    if (ouverte) void preparer();
  },
);
// Changer d'écran (retour arrière, visite guidée) referme la modale : elle
// appartient à l'écran où on l'a ouverte. Seulement une VRAIE navigation,
// partie d'un écran : le premier chargement de `/nouvelle-demande` se
// redirige vers « Mes demandes » après avoir ouvert la modale, et ne doit pas
// la refermer.
const retirer = router.afterEach((to, from, echec) => {
  if (!echec && from.matched.length && to.path !== from.path) fermer();
});
onBeforeUnmount(retirer);
// Changer de compte (démo, rôle) change aussi les sociétés de rattachement.
watch(
  () => auth.user?.userId,
  () => {
    societesChargees.value = false;
  },
);

async function envoyer(): Promise<void> {
  for (const k of Object.keys(erreurs)) delete erreurs[k];
  if (refusFichiers.value.length) return;
  if (periodique.value && refusRegle(regle.value)) {
    toast.error(refusRegle(regle.value)!);
    return;
  }
  envoi.value = true;
  try {
    let d = inqom.value
      ? await api.post<DemandeDTO>('/demandes/dossier-inqom', { ...dossier.value, clientId: clientId.value || undefined })
      : await api.post<DemandeDTO>('/demandes', {
          objet: objet.value,
          description: description.value,
          typeId: Number(typeId.value),
          urgenceId: Number(urgenceId.value),
          ...(pourCompte.value ? { contactId: contactId.value || undefined } : { clientId: clientId.value || undefined }),
        });
    // Les fichiers suivent la demande, un par un (nginx plafonne une requête à 25 Mo).
    for (const f of fichiers.value) {
      try {
        d = await api.upload<DemandeDTO>(`/demandes/${d.id}/pieces-jointes`, f);
      } catch (err) {
        toast.error(err instanceof ApiError ? err.message : `${f.name} : envoi impossible`);
      }
    }
    if (periodique.value && !inqom.value) {
      const contact = pourCompte.value ? contactId.value : societes.value.find((x) => x.clientId === (clientId.value || societes.value[0]?.clientId))?.contactId;
      try {
        await api.post('/periodicites', {
          objet: objet.value,
          description: description.value,
          typeId: Number(typeId.value),
          urgenceId: Number(urgenceId.value),
          contactId: contact,
          regle: regle.value,
        });
        toast.success('Règle de périodicité enregistrée');
      } catch (err) {
        toast.error(err instanceof ApiError ? err.message : 'La périodicité n’a pas pu être enregistrée');
      }
    }
    store.courante = d;
    toast.success(`Demande ${d.numero} envoyée`);
    fermer();
    // La fiche s'ouvre : on y voit aussitôt l'état, le numéro et les pièces jointes parties.
    await router.push(`/demandes/${d.id}`);
  } catch (err) {
    if (err instanceof ApiError && err.details) {
      for (const d of err.details) erreurs[d.path] = d.message;
      // Le formulaire Inqom est long : le champ fautif peut être hors de vue, on dit ce qui bloque.
      if (inqom.value) toast.error(err.message);
    } else {
      toast.error(err instanceof ApiError ? err.message : 'Envoi impossible');
    }
  } finally {
    envoi.value = false;
  }
}
</script>
